import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import FormWrapper from '../../FormWrapper';
import { Button, luxColors } from '@alucio/lux-ui';
import InputComponent from 'src/components/Publishers/InputComponent';
import { confirmSignIn } from 'aws-amplify/auth'
import withPasswordChecker, { ValidationType } from '../PasswordChecker';
import PasswordCriterias from '../PasswordCriterias';
import useSignedIn from 'src/components/Authenticator/services/SignedStateProvider';
import * as logger from 'src/utils/logger'
import { useLocation } from 'src/router';
import { getAuthenticationCopy } from '../../authenticationLayout';

const styles = StyleSheet.create({
  disabledButton: {
    backgroundColor: luxColors.disabled.quaternary,
    borderColor: luxColors.disabled.quaternary,
  },
  disabledTextColor: {
    color: luxColors.disabled.quinary,
  },
  invalidMessage: {
    color: luxColors.error.primary,
    fontSize: 12,
  },
  logInButton: {
    backgroundColor: luxColors.thumbnailBorder.primary,
    borderColor: luxColors.thumbnailBorder.primary,
    borderRadius: 50,
    marginBottom: 24,
    marginTop: 24,
  },
  mainWrapper: {
    flexDirection: 'column',
    width: '100%',
  },
  marginTop: {
    marginTop: 10,
  },
  row: {
    flexDirection: 'row',
  },
  securityItem: {
    alignItems: 'center',
    marginBottom: 5,
  },
  securityItemText: {
    color: luxColors.text.secondary,
    fontSize: 13,
  },
  securityWrapper: {
    flex: 1,
    flexDirection: 'column',
  },
});

interface Props {
  isPasswordValid: boolean;
  onPasswordChange: (pswd: string) => void,
  onRepeatedPasswordChange: (pswd: string) => void,
  password: string;
  passwordValidations: ValidationType;
  repeatedPassword: string;
}

const RequireNewPassword = (props: Props) => {
  const {
    isPasswordValid,
    onPasswordChange,
    onRepeatedPasswordChange,
    password,
    passwordValidations,
    repeatedPassword,
  } = props;
  const [disabled, setDisabled] = useState<boolean>(false);
  const isSubmitDisabled = !isPasswordValid || disabled;
  const { username } = useSignedIn();

  async function changePassword(): Promise<void> {
    setDisabled(true);
    try {
      await confirmSignIn({
        challengeResponse: password,
      })
      const email = username;
      // Identify new user
      analytics?.identify(email, {}, function () {
        analytics?.track('LOGIN_FIRST_LOGIN', {
          action: 'FIRST_LOGIN',
          category: 'LOGIN',
        });
      });
      window.location.reload()
    } catch (e) {
      logger.requireNewPassword.error('unexpected error happened within changePassword function', e);
      setDisabled(false);
    }
  }

  return (
    <View style={styles.mainWrapper}>
      <PasswordCriterias passwordValidations={passwordValidations} />
      <View style={styles.marginTop}>
        <InputComponent
          disabled={disabled}
          isPassword={true}
          removeMarginPadding={true}
          required={false}
          title="PASSWORD"
          testID="password-new"
          placeHolder="Password"
          titleColor={luxColors.text.primary}
          value={password}
          onChangeText={onPasswordChange}
        />
      </View>
      <View style={styles.marginTop}>
        <InputComponent
          disabled={disabled}
          isPassword={true}
          removeMarginPadding={true}
          required={false}
          title="CONFIRM PASSWORD"
          testID="password-confirm"
          placeHolder="Confirm Password"
          titleColor={luxColors.text.primary}
          value={repeatedPassword}
          onSubmitEditing={isSubmitDisabled ? () => null : changePassword}
          onChangeText={onRepeatedPasswordChange}
        />
      </View>
      <Button.Kitten
        disabled={isSubmitDisabled}
        style={[styles.logInButton, isSubmitDisabled && styles.disabledButton]}
        onPress={changePassword}
      >
        <Text testID="submit-password" style={[isSubmitDisabled && styles.disabledTextColor]}>Set password</Text>
      </Button.Kitten>
    </View>
  );
};

const RequireNewPasswordWrapper = (props) => {
  const location = useLocation();
  const authenticationCopy = getAuthenticationCopy(location.search);

  return (
    <FormWrapper title={authenticationCopy.setPasswordTitle}>
      <RequireNewPassword {...props} />
    </FormWrapper>
  );
};

RequireNewPasswordWrapper.displayName = 'RequireNewPassword';

export default withPasswordChecker(RequireNewPasswordWrapper);
